import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Spinner, Alert, Card, Button, Form, Row, Col, Dropdown, Badge } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import MobileListLayout from '../common/MobileListLayout';
import { usePageTitle } from '../context/PageTitleContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { getPromozioni, deletePromozione } from '../services/api'; // Importa le nuove funzioni API
import ActionToolbar from '../common/ActionToolbar';
import SortController from '../common/SortController';
import SortableHeader from '../common/SortableHeader';
import ColumnOrderController from '../common/ColumnOrderController';
import { checklistFilterFn } from '../common/clientFilterUtils';

// Helper per la creazione delle colonne
const columnHelper = createColumnHelper();

// Definizione di tutte le colonne disponibili
const allColumnsMap = {
    id: "ID",
    nome: "Nome Promozione",
    descrizione: "Descrizione",
    immagine_url: "Immagine",
    is_active: "Attiva",
    data_inizio: "Data Inizio",
    data_fine: "Data Fine",
    tipo_sconto: "Tipo Sconto",
    valore_sconto: "Valore Sconto",
    target_item_type: "Tipo Articolo Target",
    target_categoria_genere: "Categoria/Genere Target",
    target_supporto: "Supporto Target",
    target_casa_di_produzione: "Casa di Produzione Target",
    target_brand: "Brand Target",
    target_licenza: "Licenza Target",
    data_creazione: "Data Creazione",
    data_aggiornamento: "Data Aggiornamento",
};

const initialVisibleColumns = ['id', 'nome', 'is_active', 'data_inizio', 'data_fine', 'tipo_sconto', 'valore_sconto', 'target_item_type'];
const visibilityKey = 'table-visibility-promozioni';

function PromozioniList() {
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const [promozioni, setPromozioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPromozioneIds, setSelectedPromozioneIds] = useState([]);

  // TanStack Table states
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem(visibilityKey);
    if (saved) {
      return JSON.parse(saved);
    }
    return Object.keys(allColumnsMap).reduce((acc, key) => {
      acc[key] = initialVisibleColumns.includes(key);
      return acc;
    }, {});
  });
  const [sorting, setSorting] = useState(() => {
    const saved = sessionStorage.getItem('list-sorting-promozioni');
    return saved ? JSON.parse(saved) : [];
  });
  const [columnFilters, setColumnFilters] = useState(() => {
    const saved = sessionStorage.getItem('list-filters-promozioni');
    return saved ? JSON.parse(saved) : [];
  });
  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem('table-column-order-promozioni');
    const defaultOrder = ['select', ...Object.keys(allColumnsMap)];
    if (savedOrder) {
        let parsedOrder = JSON.parse(savedOrder);
        parsedOrder = parsedOrder.filter(c => c !== 'select');
        return ['select', ...parsedOrder];
    }
    return defaultOrder;
  });
  const [columnSizing, setColumnSizing] = useState(() => {
    const saved = localStorage.getItem('table-column-sizing-promozioni');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    sessionStorage.setItem('list-sorting-promozioni', JSON.stringify(sorting));
  }, [sorting]);

  useEffect(() => {
    sessionStorage.setItem('list-filters-promozioni', JSON.stringify(columnFilters));
  }, [columnFilters]);

  useEffect(() => {
    localStorage.setItem('table-column-order-promozioni', JSON.stringify(columnOrder));
  }, [columnOrder]);

  useEffect(() => {
    localStorage.setItem('table-column-sizing-promozioni', JSON.stringify(columnSizing));
  }, [columnSizing]);

  const fetchPromozioniData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPromozioni(); // Chiamata API
      setPromozioni(response.data);
    } catch (err) {
      setError('Errore nel caricamento delle promozioni.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPromozioniData();
  }, [fetchPromozioniData]);

  const getColumnValue = (promozione, columnName) => {
    if (columnName === 'immagine_url' && promozione.immagine_url) {
      return <img 
        src={promozione.immagine_url && promozione.immagine_url.startsWith('http') 
        ? promozione.immagine_url 
        : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${promozione.immagine_url}`} 
        alt="Immagine Promozione" style={{ width: '50px', height: 'auto' }} 
        onError={(e) => { e.target.src = '/placeholder.jpg'; }} // Fallback image
      />;
    }
    if (columnName === 'is_active') {
        return promozione.is_active ? 'Sì' : 'No';
    }
    if (columnName === 'data_inizio' || columnName === 'data_fine' || columnName === 'data_creazione' || columnName === 'data_aggiornamento') {
        return promozione[columnName] ? new Date(promozione[columnName]).toLocaleDateString() : 'N/D';
    }
    if (columnName === 'valore_sconto') {
        return `${promozione.valore_sconto}%`;
    }
    return promozione[columnName] !== undefined && promozione[columnName] !== null ? String(promozione[columnName]) : 'N/D';
  };

  // Helper functions for mobile view
  const formatDate = (dateString) => {
    if (!dateString) return 'N/D';
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  const renderPromozioneCard = useCallback((promozione) => {
    const imageUrl = promozione.immagine_url && promozione.immagine_url.startsWith('http')
      ? promozione.immagine_url
      : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${promozione.immagine_url}`;

    return (
      <Card className="mb-2" style={{ fontSize: '0.9rem' }}>
        <Card.Body>
          <Row>
            <Col xs={promozione.immagine_url ? 8 : 12}>
              <div className="d-flex justify-content-between align-items-start mb-2">
                <strong style={{ fontSize: '1rem' }}>{promozione.nome}</strong>
                <Badge bg={promozione.is_active ? 'success' : 'secondary'}>
                  {promozione.is_active ? 'Attiva' : 'Non Attiva'}
                </Badge>
              </div>
              
              {promozione.descrizione && (
                <div className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
                  {promozione.descrizione}
                </div>
              )}

              <Row className="mb-2">
                <Col xs={6}>
                  <small className="text-muted">Tipo Sconto:</small>
                  <div>{promozione.tipo_sconto || 'N/D'}</div>
                </Col>
                <Col xs={6}>
                  <small className="text-muted">Valore:</small>
                  <div><strong>{promozione.valore_sconto}%</strong></div>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col xs={6}>
                  <small className="text-muted">Inizio:</small>
                  <div>{formatDate(promozione.data_inizio)}</div>
                </Col>
                <Col xs={6}>
                  <small className="text-muted">Fine:</small>
                  <div>{formatDate(promozione.data_fine)}</div>
                </Col>
              </Row>

              {(promozione.target_item_type || promozione.target_categoria_genere || promozione.target_supporto) && (
                <div className="mt-2 pt-2 border-top">
                  <small className="text-muted">Target:</small>
                  <div style={{ fontSize: '0.85rem' }}>
                    {promozione.target_item_type && <Badge bg="info" className="me-1">{promozione.target_item_type}</Badge>}
                    {promozione.target_categoria_genere && <Badge bg="info" className="me-1">{promozione.target_categoria_genere}</Badge>}
                    {promozione.target_supporto && <Badge bg="info" className="me-1">{promozione.target_supporto}</Badge>}
                  </div>
                </div>
              )}

              <div className="d-flex gap-2 mt-3">
                <Link to={`/promozioni/${promozione.id}`}>
                  <Button variant="primary" size="sm">Modifica</Button>
                </Link>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={async () => {
                    if (window.confirm(`Eliminare la promozione "${promozione.nome}"?`)) {
                      try {
                        await deletePromozione(promozione.id);
                        fetchPromozioniData();
                      } catch (err) {
                        alert('Errore durante l\'eliminazione');
                      }
                    }
                  }}
                >
                  Elimina
                </Button>
              </div>
            </Col>

            {promozione.immagine_url && (
              <Col xs={4} className="text-end">
                <img
                  src={imageUrl}
                  alt={promozione.nome}
                  style={{ width: '100%', height: 'auto', maxHeight: '120px', objectFit: 'cover', borderRadius: '4px' }}
                  onError={(e) => { e.target.src = '/placeholder.jpg'; }}
                />
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>
    );
  }, [fetchPromozioniData]);


  // Valori distinti calcolati sui dati gia' caricati (niente chiamata server: questa
  // pagina fetcha sempre tutte le promozioni e filtra in memoria).
  const distinctFilterValues = useMemo(() => {
    const campi = ['tipo_sconto', 'target_item_type', 'target_categoria_genere', 'target_supporto', 'target_casa_di_produzione', 'target_brand', 'target_licenza'];
    return campi.reduce((acc, campo) => {
      acc[campo] = [...new Set(promozioni.map(p => p[campo]).filter(Boolean))];
      return acc;
    }, {});
  }, [promozioni]);

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <Form.Check
            type="checkbox"
            onChange={table.getToggleAllRowsSelectedHandler()}
            checked={table.getIsAllRowsSelected()}
            disabled={promozioni.length === 0}
          />
        ),
        cell: ({ row }) => (
          <Form.Check
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
          />
        ),
        minSize: 30,
        maxSize: 30,
        size: 30,
        enableResizing: false,
        enableSorting: false,
        enableColumnFilter: false,
      }),
      ...Object.entries(allColumnsMap).map(([key, value]) => {
        const campiSelect = ['is_active', 'tipo_sconto', 'target_item_type', 'target_categoria_genere', 'target_supporto', 'target_casa_di_produzione', 'target_brand', 'target_licenza'];
        const isSelect = campiSelect.includes(key);
        return columnHelper.accessor(key, {
            header: value,
            cell: info => getColumnValue(info.row.original, key),
            enableColumnFilter: true,
            filterFn: isSelect ? checklistFilterFn : 'includesString',
            meta: {
                filterVariant: isSelect ? 'select' : 'text',
                filterOptions: key === 'is_active' ? [{ value: true, label: 'Sì' }, { value: false, label: 'No' }] : distinctFilterValues[key],
            },
        });
      }),
    ],
    [promozioni, distinctFilterValues]
  );

  const onColumnVisibilityChange = (updater) => {
    const newState = typeof updater === 'function' ? updater(columnVisibility) : updater;
    setColumnVisibility(newState);
    localStorage.setItem(visibilityKey, JSON.stringify(newState));
  };
  
  const table = useReactTable({
    data: promozioni,
    columns,
    state: {
      columnVisibility,
      sorting,
      columnFilters,
      columnOrder,
      columnSizing,
    },
    onColumnVisibilityChange: onColumnVisibilityChange,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    enableColumnReordering: true,
    enableMultiSort: true,
  });

  const handleAutoResize = (column) => {
    const headerText = String(column.columnDef.header);
    const cells = table.getRowModel().rows.map(row => {
      const value = row.original[column.id];
      return value !== undefined && value !== null ? String(value) : '';
    });
    
    const charMultiplier = 8;
    const padding = 30;

    const headerWidth = headerText.length * charMultiplier;
    const maxWidth = cells.reduce((max, cell) => {
      return Math.max(max, cell.length * charMultiplier);
    }, headerWidth);

    const newSize = Math.ceil(maxWidth) + padding;

    table.setColumnSizing(prev => ({
      ...prev,
      [column.id]: newSize,
    }));
  };

  useEffect(() => {
    setSelectedPromozioneIds(table.getSelectedRowModel().rows.map(row => row.original.id));
  }, [table, table.getSelectedRowModel().rows]);

  const handleRowClick = (row) => {
    row.toggleSelected();
  };

  const deleteApiMap = { promozioni: deletePromozione };
  // Non abbiamo ancora un endpoint deleteMultiplePromozioni, quindi lo lasciamo vuoto
  const deleteMultipleApiMap = { promozioni: null };

  const handleResetFilters = () => {
    table.resetColumnFilters();
    setSorting([]);
  };

  usePageTitle(`Promozioni (${promozioni.length})`);

  return (
    <>
      <Card style={{ marginBottom: '80px' }}>
        <Card.Header className="sticky-card-header">
          <Row className="align-items-center">
            <Col md={6} className="d-flex justify-content-start align-items-center">
              <Button as={Link} to="/promozioni/new" className="me-2" size="sm" variant="success">
                <FontAwesomeIcon icon={faPlus} className="me-1" /> Nuova Promozione
              </Button>
            </Col>
            <Col md={6} className="d-flex justify-content-end align-items-center">
              <Dropdown className="me-2" autoClose="outside">
                <Dropdown.Toggle variant="outline-secondary" id="dropdown-sorting" size="sm">
                  Ordinamento
                </Dropdown.Toggle>
                <Dropdown.Menu className="long-dropdown-menu p-2">
                  <SortController
                    columns={table.getAllLeafColumns()}
                    sorting={sorting}
                    setSorting={setSorting}
                  />
                </Dropdown.Menu>
              </Dropdown>

              <Dropdown className="me-2" autoClose="outside">
                <Dropdown.Toggle variant="outline-secondary" id="dropdown-columns" size="sm">
                  Colonne
                </Dropdown.Toggle>
                <Dropdown.Menu className="long-dropdown-menu p-0" style={{ minWidth: '500px' }}>
                  <div className="d-flex">
                    <div className="p-2" style={{ flex: 1, borderRight: '1px solid #dee2e6' }}>
                      <ColumnOrderController
                        table={table}
                        allColumnsMap={allColumnsMap}
                      />
                    </div>
                    <div className="p-2" style={{ flex: 1 }}>
                      <h6 className="fw-bold">Mostra Colonne</h6>
                      <div className="d-flex flex-column">
                        {table.getAllColumns().filter(column => column.id !== 'select').map(column => (
                          <Form.Check
                            key={column.id}
                            type="checkbox"
                            label={allColumnsMap[column.id]}
                            checked={column.getIsVisible()}
                            onChange={column.getToggleVisibilityHandler()}
                            className="dropdown-item"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </Dropdown.Menu>
              </Dropdown>
              <Button
                  variant="outline-secondary"
                  size="sm"
                  className="ms-2"
                  onClick={handleResetFilters}
                  disabled={columnFilters.length === 0 && sorting.length === 0}
              >
                  Azzera Filtri
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {loading && <div className="text-center"><Spinner animation="border" /></div>}
          {error && <Alert variant="danger">{error}</Alert>}
          {!loading && !error && (
            <>
              {isMobile ? (
                <MobileListLayout
                  items={promozioni}
                  renderCard={renderPromozioneCard}
                  entityName="promozione"
                  entityNamePlural="promozioni"
                  createPath="/promozioni/new"
                  createButtonText="Nuova Promozione"
                  showSelectAll={false}
                />
              ) : (
                <div style={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
                  <Table striped bordered hover style={{ width: table.getTotalSize() }} className="aligned-table">
                <thead>
                  {table.getHeaderGroups().map(headerGroup => (
                    <React.Fragment key={headerGroup.id}>
                      <tr>
                        {headerGroup.headers.map(header => (
                          <SortableHeader key={header.id} header={header} table={table} handleAutoResize={handleAutoResize} />
                        ))}
                      </tr>

                    </React.Fragment>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map(row => (
                    <tr key={row.id} onClick={() => handleRowClick(row)} className={row.getIsSelected() ? 'table-primary' : ''} style={{ cursor: 'pointer', height: '40px' }}>
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} style={{ verticalAlign: 'middle', width: `${cell.column.getSize()}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
              )}
            </>
          )}
          <ActionToolbar
            selectedItemIds={selectedPromozioneIds}
            entityType="promozioni"
            onDeleteSuccess={fetchPromozioniData}
            deleteApiMap={deleteApiMap}
            deleteMultipleApiMap={deleteMultipleApiMap}
          />
        </Card.Body>
      </Card>
    </>
  );
}

export default PromozioniList;